import { useEffect } from "react";

import { useConfigurationContext } from "../root/ConfigurationContext";

/**
 * MuscoBrandProvider — aplica los colores "del alma" del CRM como CSS
 * variables sobrescribibles en runtime.
 *
 * Los colores personalizables son:
 *   --musco-accent             (default #FFD200 amarillo Musco)
 *   --musco-accent-foreground  (default #0a0a0a negro sobre amarillo)
 *   --musco-accent2            (default #0064A6 azul Endeavour Musco)
 *   --musco-accent2-foreground (default #ffffff blanco sobre azul)
 *
 * La fuente de verdad es `configuration.config.theme`, persistida en
 * Supabase. Como fallback inmediato (antes de cargar el config) usamos
 * los defaults del CSS y un cache en localStorage para no parpadear.
 *
 * Los neutros (bg, panel, line, text, muted) NO se personalizan — están
 * fijos en index.css según el modo light/dark para garantizar contraste.
 *
 * Para cambiar los colores: ir a Settings → Identidad visual.
 */

const STORAGE_KEY = "musco.brand";
const VARIABLES = [
  "musco-accent",
  "musco-accent-foreground",
  "musco-accent2",
  "musco-accent2-foreground",
] as const;

type BrandVar = (typeof VARIABLES)[number];
export type MuscoBrandConfig = Partial<Record<BrandVar, string>>;

export const DEFAULT_BRAND: Record<BrandVar, string> = {
  "musco-accent": "#FFD200",
  "musco-accent-foreground": "#0a0a0a",
  "musco-accent2": "#0064A6",
  "musco-accent2-foreground": "#ffffff",
};

function applyBrand(brand: MuscoBrandConfig) {
  const root = document.documentElement;
  VARIABLES.forEach((v) => {
    const value = brand[v];
    if (value && /^#[0-9a-fA-F]{3,8}$/.test(value)) {
      root.style.setProperty(`--${v}`, value);
    } else {
      // Si el valor no es válido, removemos el override para que use el default
      root.style.removeProperty(`--${v}`);
    }
  });

  // Para que shadcn-admin-kit (botones, focus rings) también respete el
  // amarillo personalizado, mapeamos --musco-accent a las vars de shadcn.
  // Usamos un truco: sobrescribimos --primary, --accent, --ring directamente
  // a la versión hex del usuario (shadcn acepta hex también).
  if (brand["musco-accent"]) {
    root.style.setProperty("--primary", brand["musco-accent"]);
    root.style.setProperty(
      "--primary-foreground",
      brand["musco-accent-foreground"] || "#0a0a0a",
    );
    root.style.setProperty("--accent", brand["musco-accent"]);
    root.style.setProperty(
      "--accent-foreground",
      brand["musco-accent-foreground"] || "#0a0a0a",
    );
    root.style.setProperty("--ring", brand["musco-accent"]);
    root.style.setProperty("--chart-1", brand["musco-accent"]);
  }
}

/**
 * Carga los colores guardados en localStorage al primer render (antes de
 * que el ConfigurationContext esté listo). Evita el flash.
 */
export function bootstrapBrand() {
  if (typeof window === "undefined") return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as MuscoBrandConfig;
      applyBrand(parsed);
    }
  } catch {
    // ignorar errores de parseo
  }
}

/**
 * Hook + componente que sincroniza el ConfigurationContext (que viene de
 * Supabase) con las CSS variables. Llamado una vez en CRM.tsx.
 */
export function MuscoBrandProvider({ children }: { children: React.ReactNode }) {
  const config = useConfigurationContext() as any;
  const brand: MuscoBrandConfig = config?.brand ?? {};

  useEffect(() => {
    applyBrand(brand);
    // Cache local para próxima carga (evita flash)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(brand));
    } catch {
      // ignorar quota errors
    }
  }, [brand]);

  return <>{children}</>;
}

/**
 * Hook para componentes que quieran preview de un cambio antes de guardar
 * (e.g. el color picker en Settings).
 */
export function previewBrand(brand: MuscoBrandConfig) {
  applyBrand({ ...DEFAULT_BRAND, ...brand });
}

export function resetBrandPreview(currentBrand: MuscoBrandConfig | null) {
  applyBrand(currentBrand ?? {});
}
